import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Input, Upload, Spin, message, Modal, Typography, Tooltip, Dropdown, Space, Divider, Badge } from 'antd';
import { FolderOpenOutlined, InboxOutlined, UploadOutlined, FolderAddOutlined, CloudUploadOutlined, FileOutlined } from "@ant-design/icons";
import { createFolder, uploadFiles, uploadFolderFile } from '../../../../services/inicio/repositorio/repositorioDescargarVerService';

const { Text } = Typography;
const { Dragger } = Upload;

const CrearCarpeta = ({
    parentId,
    diseno,
    onFolderCreated,
    currentPath,
    username,
    pathActual,
    onFileUploaded,
    onFolderUploaded
}) => {
    const [openModal, setOpenModal] = useState(false);
    const [openModalFile, setOpenModalFile] = useState(false);
    const [openModalFolder, setOpenModalFolder] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const BASE_PATH = "Repositorio/";
    const [uploadingCount, setUploadingCount] = useState(0);
    const [folderFiles, setFolderFiles] = useState([]);
    const folderInputRef = useRef(null);
    const modalFolderRef = useRef(null);
    const [failedUploads, setFailedUploads] = useState([]);
    const [showErrors, setShowErrors] = useState(false);

    // Agregar efecto para limpiar input cuando modal se cierra
    useEffect(() => {
        if (!openModalFolder && folderInputRef.current) {
            folderInputRef.current.value = '';
            setFolderFiles([]);
        }
    }, [openModalFolder]);

    const startUpload = () => {
        setUploadingCount(prev => {
            const next = prev + 1;
            setLoading(true);
            return next;
        });
    };

    const finishUpload = () => {
        setUploadingCount(prev => {
            const next = prev - 1;
            if (next <= 0) {
                setLoading(false);
                return 0;
            }
            return next;
        });
    };

    const normalizeRepoPath = (path = "") => {
        return path
            .replace(/^DocumentosFAO\/?/, "")
            .replace(/\\/g, "/")
            .replace(/\/+$/, "");
    };

    const finalPathActual =
        pathActual && pathActual.trim() !== ""
            ? normalizeRepoPath(pathActual)
            : normalizeRepoPath(BASE_PATH + "/" + currentPath.replace(/ > /g, "/"));

    const itemsAcciones = [
        {
            key: "createFolder",
            label: "Crear carpeta",
            icon: <FolderAddOutlined />,
            onClick: () => handleOpen()
        },
        { key: "divider1", type: "divider" },
        {
            key: "uploadFolder",
            label: "Subir carpeta",
            icon: <CloudUploadOutlined />,
            onClick: () => handleOpenFolder()
        },
        { key: "divider2", type: "divider" },
        {
            key: "uploadFile",
            label: "Subir archivo",
            icon: <UploadOutlined />,
            onClick: () => handleOpenFile()
        },
        { key: "divider3", type: "divider" },
    ];

    const handleOpen = () => setOpenModal(true);
    const handleOpenFile = () => setOpenModalFile(true);

    const handleOpenFolder = () => {
        setOpenModalFolder(true);
    };

    const handleClose = () => {
        setOpenModal(false);
        form.resetFields();
        setLoading(false);
    };

    const handleCloseFile = () => {
        setOpenModalFile(false);
        setLoading(false);
    };

    const handleCloseFolder = () => {
        setOpenModalFolder(false);
        setLoading(false);
        setFolderFiles([]);
        if (folderInputRef.current) {
            folderInputRef.current.value = '';
        }
    };

    // Props para subir archivos individuales (usando Dragger)
    const uploadProps = {
        name: "file",
        multiple: true,
        customRequest: async (options) => {
            const { file, onSuccess, onError } = options;
            const formData = new FormData();
            startUpload();
            formData.append("file", file);
            formData.append("parentPath", finalPathActual);
            formData.append("parentId", parentId);
            formData.append("username", username);
            try {
                const result = await uploadFiles(formData, finalPathActual);
                onSuccess(result, file);
                if (onFileUploaded && result.files?.length > 0) {
                    onFileUploaded(result.files[0]); 
                }
                message.success(`${file.name} subido correctamente`);
                
            } catch (err) {
                onError(err);
                console.error("❌ Error archivo:", file.name, err);
                setFailedUploads((prev) => [
                    ...prev,
                    {
                        name: file.name,
                        error: err?.response?.data?.message || "Error desconocido",
                    },
                ]);

                message.error(`${file.name} error al subir.`);
            } finally {
                finishUpload();
            }
        },
    };

    const FOLDER_LIMITS = {
        MAX_FILES: 500,
        MAX_TOTAL_SIZE_MB: 300,
        MAX_FILE_SIZE_MB: 50,
        MAX_DEPTH: 10,
    };

    // Función para validar los archivos de la carpeta antes de subir
    const validateFolderFiles = (files) => {
        if (!files || files.length === 0) {
            return { ok: false, message: "La carpeta está vacía" };
        }

        // 1️⃣ Cantidad de archivos
        if (files.length > FOLDER_LIMITS.MAX_FILES) {
            return {
                ok: false,
                message: `La carpeta tiene demasiados archivos (${files.length}). Máximo permitido: ${FOLDER_LIMITS.MAX_FILES}`,
            };
        }

        let totalSize = 0;
        let maxDepth = 0;

        for (const file of files) {
            // 2️⃣ Tamaño por archivo
            const sizeMB = file.size / (1024 * 1024);
            if (sizeMB > FOLDER_LIMITS.MAX_FILE_SIZE_MB) {
                return {
                    ok: false,
                    message: `El archivo "${file.name}" supera el tamaño permitido (${FOLDER_LIMITS.MAX_FILE_SIZE_MB} MB)`,
                };
            }

            totalSize += file.size;

            // 3️⃣ Profundidad de carpetas
            if (file.webkitRelativePath) {
                const depth = file.webkitRelativePath.split("/").length - 1;
                maxDepth = Math.max(maxDepth, depth);
            }
        }

        // 4️⃣ Tamaño total
        const totalSizeMB = totalSize / (1024 * 1024);
        if (totalSizeMB > FOLDER_LIMITS.MAX_TOTAL_SIZE_MB) {
            return {
                ok: false,
                message: `La carpeta pesa demasiado (${totalSizeMB.toFixed(
                    1
                )} MB). Máximo permitido: ${FOLDER_LIMITS.MAX_TOTAL_SIZE_MB} MB`,
            };
        }

        if (maxDepth > FOLDER_LIMITS.MAX_DEPTH) {
            return {
                ok: false,
                message: `La estructura es demasiado profunda (${maxDepth} niveles). Máximo permitido: ${FOLDER_LIMITS.MAX_DEPTH}`,
            };
        }

        return { ok: true };
    };


    // Función mejorada para manejar el input de carpeta
    const handleNativeFolderUpload = async (event) => {
        const files = Array.from(event.target.files);

        if (!files || files.length === 0) {
            return;
        }

        /* =========================
         * 1️⃣ VALIDACIÓN DE LÍMITES
         * ========================= */
        const validation = validateFolderFiles(files);
        if (!validation.ok) {
            message.error(validation.message);
            event.target.value = '';
            return;
        }

        /* =========================
         * 2️⃣ DETECTAR FOLDER ROOT
         * ========================= */
        let hasWebkitRelativePath = false;
        let folderName = '';

        for (let i = 0; i < Math.min(files.length, 3); i++) {
            const file = files[i];

            if (file.webkitRelativePath) {
                hasWebkitRelativePath = true;

                const parts = file.webkitRelativePath.split('/');
                if (parts.length > 1 && !folderName) {
                    folderName = parts[0]; // carpeta raíz
                }
            }
        }

        /* =========================
         * 3️⃣ FALLBACK DE NAVEGADOR
         * ========================= */
        if (!hasWebkitRelativePath) {
            console.warn('⚠️ ADVERTENCIA: webkitRelativePath NO está disponible');

            const input = event.target;
            const hasWebkitDir = input.hasAttribute('webkitdirectory');
            const hasDirectory = input.hasAttribute('directory');

            if (!hasWebkitDir && !hasDirectory) {
                message.error(
                    'Error: El navegador no permite seleccionar carpetas. Usa Chrome o Edge.'
                );
                event.target.value = '';
                return;
            }

            folderName = 'Carpeta_' + new Date().toISOString().slice(0, 10);
        }

        /* =========================
         * 4️⃣ SUBIDA FINAL
         * ========================= */
        try {
            await processFolderUpload(files, folderName);
        } finally {
            // limpiar input SIEMPRE
            event.target.value = '';
        }
    };


    // ✅ Lee carpetas arrastradas de forma recursiva
    const readDroppedItems = async (items) => {
        const files = [];

        const traverseEntry = async (entry, path = '') => {
            if (entry.isFile) {
                await new Promise((resolve) => {
                    entry.file((file) => {
                        // Simulamos webkitRelativePath
                        Object.defineProperty(file, 'webkitRelativePath', {
                            value: path + file.name,
                        });
                        files.push(file);
                        resolve();
                    });
                });
            }
            else if (entry.isDirectory) {
                const reader = entry.createReader();
                const entries = await new Promise((resolve) => {
                    reader.readEntries(resolve);
                });

                for (const subEntry of entries) {
                    await traverseEntry(subEntry, path + entry.name + '/');
                }
            }
        };

        for (const item of items) {
            const entry = item.webkitGetAsEntry?.();
            if (entry) {
                await traverseEntry(entry);
            }
        }

        return files;
    };

    // FUNCIÓN SIMPLIFICADA Y CORREGIDA PARA PROCESAR CARPETA
    const processFolderUpload = async (files, folderName = '') => {
        setLoading(true);
        setFolderFiles(files);
        try {


            const formData = new FormData();

            // 1️⃣ Agregar archivos
            files.forEach(file => {
                formData.append("files", file);
            });

            // 2️⃣ Enviar TODAS las rutas relativas en un solo JSON
            const pathsArray = files.map(file => file.webkitRelativePath || file.name);
            formData.append("paths", JSON.stringify(pathsArray));

            // 3️⃣ Metadatos
            formData.append("parentId", parentId);
            formData.append("parentPath", finalPathActual);
            formData.append("username", username);
            formData.append("folderName", folderName);
            const response = await uploadFolderFile(formData);
            if (onFolderUploaded && response.data?.estructura) {
                onFolderUploaded(response.data.estructura);
            }

            message.success(
                `Carpeta "${folderName}" subida correctamente (${files.length} archivos)`
            );

            setTimeout(() => {
                handleCloseFolder();
            }, 1500);

        } catch (error) {
            console.error('❌ Error en processFolderUpload:', error);

            if (error.response) {
                message.error(error.response.data?.message || 'Error del servidor');
            } else if (error.request) {
                message.error('No se pudo conectar con el servidor');
            } else {
                message.error('Error al procesar la carpeta');
            }

        } finally {
            setLoading(false);
        }
    };


    // FUNCIÓN MEJORADA PARA ACTIVAR EL INPUT
    const triggerFolderInput = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (loading) return;
        if (folderInputRef.current) {
            // Limpiar el valor primero
            folderInputRef.current.value = '';

            // Usar setTimeout para asegurar que el clic se procese correctamente
            setTimeout(() => {
                folderInputRef.current.click();
            }, 10);
        }
    };

    const handleCreateFolder = async () => {
        try {
            startUpload();
            const values = await form.validateFields(["folderName"]);
            const name = values.folderName.trim();
            const result = await createFolder({ parentId, folderName: name, username });
            const newFolder = result.folder;
            if (onFolderCreated) onFolderCreated(newFolder);

            handleClose();
            form.resetFields(["folderName"]);
        } catch (error) {
            console.error("🔴 [CREATE_FOLDER] error:", error);
        } finally {
            finishUpload();
        }
    };

    const renderCreateButton = () => {
        if (diseno === 1) {
            return (
                <Dropdown
                    menu={{
                        items: itemsAcciones,
                        onClick: ({ key }) => {
                            if (key === "uploadFolder") handleOpenFolder();
                        }
                    }}
                    trigger={["click"]}
                >
                    <Button>Acciones</Button>
                </Dropdown>
            );
        } else if (diseno === 2) {
            return (
                <>
                    <Tooltip title="Crear carpeta">
                        <Button
                            type="primary"
                            icon={<FolderAddOutlined />}
                            onClick={handleOpen}
                            style={{ fontSize: 20, marginRight: 10 }}
                        />
                    </Tooltip>
                    <Tooltip title="Subir carpeta">
                        <Button
                            type="primary"
                            icon={<CloudUploadOutlined />}
                            onClick={handleOpenFolder}
                            style={{ fontSize: "20px" }}
                        />
                    </Tooltip>
                </>
            );
        } else {
            return (
                <Tooltip title="Subir archivo">
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={handleOpenFile}
                        style={{ fontSize: "20px" }}
                    />
                </Tooltip>
            )
        }
    };

    const RutaActual = () => {
        return (
            <div
                style={{
                    backgroundColor: "#fafafa",
                    padding: "16px",
                    borderRadius: "6px",
                    marginBottom: "16px",
                    border: "1px solid #d9d9d9",
                }}
            >
                <Divider orientation="left" orientationMargin={0}>
                    <Space>
                        <FolderOpenOutlined style={{ color: "#1677ff", fontSize: 16 }} />
                        <Text strong style={{ margin: 0 }}>
                            Ruta actual
                        </Text>
                    </Space>
                </Divider>
                <Text style={{ display: "block", marginTop: "8px" }}>{currentPath}</Text>
            </div>
        );
    };

    return (
        <>
            {renderCreateButton()}

            {/* Modal para crear carpeta */}
            <Modal
                title="Crear Carpeta"
                open={openModal}
                onCancel={handleClose}
                onOk={handleCreateFolder}
                okText="Crear"
                cancelText="Cancelar"
                width={"50%"}
                destroyOnHidden={true}
            >
                <Spin spinning={loading} tip="Creando carpeta..." size="large">
                    <Form layout="vertical" form={form}>
                        <Form.Item
                            label="Nombre de la nueva carpeta"
                            name="folderName"
                            rules={[
                                { required: true, message: "Por favor ingresa un nombre." },
                                {
                                    pattern: /^[^\\\/:*?"<>|]+$/,
                                    message: 'No usar / \\ : * ? " < > |',
                                },
                            ]}
                        >
                            <Input placeholder="Ej: Mi carpeta" />
                        </Form.Item>
                    </Form>
                    <Divider />
                    <RutaActual />
                </Spin>
            </Modal>

            {/* Modal para subir archivo */}
            <Modal
                title="Subir archivo"
                open={openModalFile}
                onCancel={handleCloseFile}
                cancelText="Cancelar"
                width="50%"
                footer={null}
                destroyOnHidden={true}
            >
                <Spin spinning={loading} tip="Subiendo archivo..." size="large">
                    <div style={{ width: "100%" }}>
                        {failedUploads.length > 0 && (
                            <div style={{ textAlign: "right", marginBottom: 10 }}>
                                <Badge count={failedUploads.length} color="red">
                                    <Button danger onClick={() => setShowErrors(true)}>
                                        ⚠️ Archivos no subidos
                                    </Button>
                                </Badge>
                            </div>
                        )}
                        <Divider />
                        <Dragger {...uploadProps} style={{ marginTop: 16, width: "80%", margin: "0 auto" }}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ fontSize: 48, color: "#999" }} />
                            </p>
                            <p className="ant-upload-text">
                                Haz clic o arrastra tus archivos a esta área para subir
                            </p>
                            <p className="ant-upload-hint">
                                Soporta subir uno o varios archivos simultáneamente.
                            </p>
                        </Dragger>
                    </div>
                    <Divider />
                    <RutaActual />
                </Spin>
                <div style={{ marginTop: 10, textAlign: "right" }}>
                    <Button type="primary" onClick={handleCloseFile}>Cerrar</Button>
                </div>
            </Modal>

            {/* Modal para subir carpeta (CON INPUT NATIVO) */}
            <Modal
                title="Subir carpeta"
                open={openModalFolder}
                onCancel={handleCloseFolder}
                cancelText="Cancelar"
                width="50%"
                footer={null}
                destroyOnHidden={true}
                maskClosable={false}
                keyboard={false}
                closable={!loading}
                ref={modalFolderRef}
            >
                <Spin spinning={loading} tip="Subiendo carpeta..." size="large">
                    <Divider />

                    {/* Input nativo oculto para seleccionar carpeta */}
                    <input
                        type="file"
                        ref={folderInputRef}
                        style={{ display: 'none' }}
                        webkitdirectory=""
                        directory=""
                        multiple
                        onChange={handleNativeFolderUpload}
                        key={openModalFolder ? 'input-active' : 'input-hidden'}
                    />

                    {/* Área de subida interactiva - MODIFICADA */}
                    <div
                        style={{
                            border: '2px dashed #d9d9d9',
                            borderRadius: 8,
                            padding: 40,
                            textAlign: 'center',
                            backgroundColor: '#fafafa',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s',
                            margin: '0 auto',
                            width: '80%',
                            opacity: loading ? 0.7 : 1
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!loading) triggerFolderInput(e);
                        }}
                        onDragOver={(e) => {
                            if (loading) return;
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.style.borderColor = '#1890ff';
                            e.currentTarget.style.backgroundColor = '#e6f7ff';
                        }}
                        onDragLeave={(e) => {
                            if (loading) return;
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.style.borderColor = '#d9d9d9';
                            e.currentTarget.style.backgroundColor = '#fafafa';
                        }}
                        onDrop={async (e) => {
                            if (loading) return;

                            e.preventDefault();
                            e.stopPropagation();

                            e.currentTarget.style.borderColor = '#d9d9d9';
                            e.currentTarget.style.backgroundColor = '#fafafa';

                            const items = Array.from(e.dataTransfer.items || []);

                            if (!items.length) {
                                message.error('No se pudo leer la carpeta arrastrada');
                                return;
                            }

                            // 1️⃣ Leer estructura completa
                            const files = await readDroppedItems(items);

                            if (!files.length) {
                                message.error('La carpeta no contiene archivos válidos');
                                return;
                            }

                            // 2️⃣ VALIDACIÓN DE LÍMITES
                            const validation = validateFolderFiles(files);
                            if (!validation.ok) {
                                message.error(validation.message);
                                return;
                            }

                            // 3️⃣ Detectar carpeta raíz de forma segura
                            let folderName = 'Carpeta';

                            if (files[0].webkitRelativePath) {
                                folderName = files[0].webkitRelativePath.split('/')[0];
                            } else {
                                folderName = 'Carpeta_' + new Date().toISOString().slice(0, 10);
                            }

                            // 4️⃣ Subida final
                            processFolderUpload(files, folderName);
                        }}


                    >
                        <InboxOutlined style={{
                            fontSize: 48,
                            color: loading ? "#bfbfbf" : "#999",
                            marginBottom: 16
                        }} />

                        <p style={{
                            fontSize: 16,
                            fontWeight: 500,
                            marginBottom: 8,
                            color: loading ? "#bfbfbf" : "#262626"
                        }}>
                            {loading ? 'Subiendo carpeta...' : 'Selecciona una carpeta completa'}
                        </p>

                        <p style={{
                            color: loading ? "#bfbfbf" : "#999",
                            fontSize: 14,
                            marginBottom: 16
                        }}>
                            Haz clic aquí o arrastra una carpeta completa
                        </p>

                        <Button
                            type="primary"
                            icon={<CloudUploadOutlined />}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                triggerFolderInput(e);
                            }}
                            loading={loading}
                            disabled={loading}
                        >
                            {loading ? 'Subiendo...' : 'Seleccionar Carpeta'}
                        </Button>

                        {/* Mostrar vista previa de los archivos seleccionados */}
                        {folderFiles.length > 0 && !loading && (() => {
                            const totalSizeBytes = folderFiles.reduce((acc, f) => acc + f.size, 0);
                            const totalSizeMB = totalSizeBytes / (1024 * 1024);

                            const maxDepth = Math.max(
                                ...folderFiles.map(f =>
                                    f.webkitRelativePath ? f.webkitRelativePath.split('/').length - 1 : 1
                                )
                            );

                            const isTooLarge = totalSizeMB > FOLDER_LIMITS.MAX_TOTAL_SIZE_MB;
                            const isTooManyFiles = folderFiles.length > FOLDER_LIMITS.MAX_FILES;
                            const hasErrors = isTooLarge || isTooManyFiles;

                            return (
                                <div
                                    style={{
                                        marginTop: 20,
                                        textAlign: 'left',
                                        maxHeight: 240,
                                        overflow: 'auto',
                                        border: `1px solid ${hasErrors ? '#ffccc7' : '#f0f0f0'}`,
                                        borderRadius: 6,
                                        padding: 12,
                                        backgroundColor: hasErrors ? '#fff1f0' : '#fff',
                                    }}
                                >
                                    {/* Header */}
                                    <p style={{ fontWeight: 500, marginBottom: 6, color: '#262626' }}>
                                        <FileOutlined /> Contenido de la carpeta seleccionada
                                    </p>

                                    {/* Resumen */}
                                    <p style={{ fontSize: 12, marginBottom: 10, color: hasErrors ? '#cf1322' : '#595959' }}>
                                        📦 {folderFiles.length} archivos — {totalSizeMB.toFixed(1)} MB — 📁 profundidad {maxDepth}
                                    </p>

                                    {hasErrors && (
                                        <p style={{ fontSize: 12, color: '#cf1322', marginBottom: 8 }}>
                                            ⚠️ Esta carpeta supera los límites permitidos. Reduce el tamaño o la cantidad de archivos.
                                        </p>
                                    )}

                                    {/* Lista de archivos */}
                                    <ul style={{ paddingLeft: 20, margin: 0, fontSize: 12 }}>
                                        {folderFiles.slice(0, 10).map((file, index) => {
                                            const fileSizeMB = file.size / (1024 * 1024);
                                            const isFileTooLarge = fileSizeMB > FOLDER_LIMITS.MAX_FILE_SIZE_MB;

                                            return (
                                                <li
                                                    key={index}
                                                    style={{
                                                        color: isFileTooLarge ? '#cf1322' : '#666',
                                                        marginBottom: 4,
                                                        wordBreak: 'break-all',
                                                        listStyleType: 'disc',
                                                    }}
                                                >
                                                    {file.webkitRelativePath || file.name}
                                                    <span style={{ marginLeft: 8, fontSize: 11 }}>
                                                        ({fileSizeMB.toFixed(2)} MB)
                                                    </span>
                                                    {isFileTooLarge && (
                                                        <span style={{ marginLeft: 6, fontSize: 11 }}>
                                                            ⚠️ supera límite
                                                        </span>
                                                    )}
                                                </li>
                                            );
                                        })}

                                        {folderFiles.length > 10 && (
                                            <li
                                                style={{
                                                    fontSize: 11,
                                                    color: '#8c8c8c',
                                                    fontStyle: 'italic',
                                                    marginTop: 6,
                                                }}
                                            >
                                                ... y {folderFiles.length - 10} archivos más
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            );
                        })}


                        <Divider style={{ margin: '20px 0' }} />

                        <div style={{
                            fontSize: 12,
                            color: '#8c8c8c',
                            textAlign: 'left',
                            backgroundColor: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: 4,
                            padding: 12
                        }}>
                            <p style={{ marginBottom: 6, fontWeight: 500, color: '#389e0d' }}>
                                <strong>📁 Instrucciones importantes:</strong>
                            </p>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li>Usa el botón "Seleccionar Carpeta" para elegir una carpeta completa</li>
                                <li>También puedes arrastrar y soltar una carpeta directamente</li>
                                <li>Se mantendrá toda la estructura interna de carpetas</li>
                                <li>Se ignorarán automáticamente archivos del sistema (.DS_Store, thumbs.db, etc.)</li>
                                <li>Límite de tamaño: 200MB por archivo</li>
                            </ul>
                        </div>
                    </div>

                    <Divider />
                    <RutaActual />
                </Spin>

                <div style={{ marginTop: 16, textAlign: "right" }}>
                    <Button
                        type="primary"
                        onClick={handleCloseFolder}
                        disabled={loading}
                    >
                        Cerrar
                    </Button>
                </div>
            </Modal>
            <Modal
                title="Archivos que no se pudieron subir"
                open={showErrors}
                onCancel={() => setShowErrors(false)}
                footer={null}
            >
                <ul>
                    {failedUploads.map((file, index) => (
                        <li key={index} style={{ marginBottom: 8 }}>
                            <strong>{file.name}</strong>
                            <br />
                            <span style={{ color: "red" }}>{file.error}</span>
                        </li>
                    ))}
                </ul>
                <Button onClick={() => setFailedUploads([])}>
                    Limpiar errores
                </Button>
            </Modal>
        </>
    );
};

export default CrearCarpeta;